import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play, Volume2, VolumeX } from "lucide-react";
import { useCatalog } from "../../context/CatalogContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const absolutize = (u) => {
  if (!u) return "";
  if (/^https?:\/\//i.test(u)) return u;
  return `${BACKEND_URL}${u}`;
};

// Fallback poster (a real jewellery shot) + fallback video (short, small).
// Admin can override via hero.craftsmanshipVideo / hero.craftsmanshipPoster.
const DEFAULT_POSTER =
  "https://images.unsplash.com/photo-1583937443351-c8b73aa4b6ff?auto=format&fit=crop&w=1600&q=85";
// User-uploaded craftsmanship video hosted on the customer-assets CDN.
const DEFAULT_VIDEO =
  "https://customer-assets.emergentagent.com/job_gold-shine-pune/artifacts/ocouzlj9_gemini_generated_video_C9D1C25A.MP4";

export default function HomeVideo() {
  const { hero } = useCatalog();
  const videoSrc = absolutize(hero?.craftsmanshipVideo) || DEFAULT_VIDEO;
  const poster = absolutize(hero?.craftsmanshipPoster) || DEFAULT_POSTER;
  const [muted, setMuted] = useState(true);
  const [started, setStarted] = useState(false);
  const videoRef = useRef(null);

  const play = () => {
    setStarted(true);
    setMuted(false);
    setTimeout(() => {
      if (videoRef.current) {
        videoRef.current.muted = false;
        videoRef.current.play();
      }
    }, 30);
  };

  const toggleMute = () => {
    setMuted((m) => {
      const nm = !m;
      if (videoRef.current) videoRef.current.muted = nm;
      return nm;
    });
  };

  return (
    <section className="hv">
      <div className="hv-sparks" aria-hidden="true">
        {Array.from({ length: 6 }).map((_, i) => (
          <span key={i} />
        ))}
      </div>
      <div className="wrap hv-inner">
        <motion.span
          className="hv-eyebrow"
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true, amount: 0.4 }}
        >
          The Craft
        </motion.span>
        <motion.h2
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          viewport={{ once: true, amount: 0.4 }}
        >
          Handmade in Rasta Peth for over 30 years.
        </motion.h2>

        <motion.div
          className="hv-player"
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          viewport={{ once: true, amount: 0.2 }}
        >
          <video
            ref={videoRef}
            src={videoSrc}
            poster={poster}
            autoPlay
            muted={muted}
            loop
            playsInline
            preload="metadata"
            controls={false}
            data-testid="home-video"
          />
          <div className="hv-overlay" />

          {!started && (
            <button
              type="button"
              className="hv-play"
              onClick={play}
              aria-label="Play with sound"
              data-testid="home-video-play"
            >
              <Play size={26} fill="#FFF" />
            </button>
          )}
          {started && (
            <button
              type="button"
              className="hv-play"
              style={{ width: 52, height: 52, top: "auto", left: "auto", right: 20, bottom: 20, transform: "none", animation: "none" }}
              onClick={toggleMute}
              aria-label={muted ? "Unmute" : "Mute"}
              data-testid="home-video-mute"
            >
              {muted ? <VolumeX size={20} /> : <Volume2 size={20} />}
            </button>
          )}

          <span className="hv-caption">Alankar Workshop · Rasta Peth</span>
        </motion.div>
      </div>
    </section>
  );
}
